export interface SellerGroupOrderProvider {
  id: string;
  seller_group_id: string;
  provider_name: string;
  active: boolean;
  created_at: Date;
  updated_at: Date;
}

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  document_type: string;
  document_number: string;
  created_at: Date;
  updated_at: Date;
  active: boolean;
}

export interface Seller {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone: string;
  company_name: string;
  trading_name: string;
  document_type: string;
  document_number: string;
  industry_id: number;
  state_registration: string | null;
  created_at: Date;
  updated_at: Date;
  active: boolean;
  timezone?: string;
}

export interface SourceAddress {
  id: string;
  zip_code: string;
  country: string;
  state: string;
  city: string;
  neighborhood: string;
  street: string;
  street_number: string;
  complement: string;
  reference: string;
  active: boolean;
  created_at: Date;
  updated_at: Date;
  seller_id: string;
  customer_id: string | null;
}

export interface DestinationAddress {
  id: string;
  zip_code: string;
  country: string;
  state: string;
  city: string;
  neighborhood: string;
  street: string;
  street_number: string;
  complement: string;
  reference: string | null;
  active: boolean;
  created_at: Date;
  updated_at: Date;
  seller_id: string | null;
  customer_id: string | null;
  latitude?: number;
  longitude?: number;
}

export interface Item {
  id: string;
  name: string;
  sku: string;
  thumb_url: string;
  quantity: number;
  amount: number;
  weight: number;
  order_id: string;
  external_id: string | null;
  created_at: Date;
}

export interface Volume {
  id: string;
  status: number;
  tracking: string;
  weight: number;
  height: number;
  length: number;
  width: number;
  cubic_volume?: number;
  invoice_id: string;
  fulfilled_at: string | null;
  created_at: Date;
  items: Item[];
}

export interface Waypoint {
  driver_name?: string;
  driver_document?: string;
  driver_phone?: string;
  message?: string;
  delivery_message?: string;
  arrived_at?: string;
  eta?: string;
  receiver_name?: string;
  receiver_document_number?: string;
  receiver_message?: string;
}

export interface DeliveryEvent {
  id: string;
  delivery_id: string;
  status: string;
  sub_status?: string;
  event_at: string;
  created_at?: string;
  failure_message?: string;
  failure_code?: string;
  failure_driver_message?: string;
  driver_name: string;
  driver_document?: string;
  driver_phone?: string;
  service_error?: string;
  observation?: string;
  manual_action?: boolean;
  latitude?: number;
  longitude?: number;
  receiver?: {
    name: string;
    document_number?: string;
    description?: string;
  };
  attachments: [
    {
      id: string;
      url: string;
      created_at: string;
      user_id?: string;
      user?: {
        email: string;
      };
    },
  ];
  user?: {
    id: string;
    email: string;
    name: string | null;
    is_seller_group_admin: boolean;
    seller_group_id: string;
    type: string;
    is_internal: boolean;
    active: boolean;
    role_id: string;
    internal_driver_document_number: string | null;
    created_at: string;
    updated_at: string;
  } | null;
}

export interface Delivery {
  driver_name: string;
  driver_document?: string;
  driver_phone?: string;
  driver_image_url?: string;
  driver_vehicle_type?: string;
  driver_vehicle_color?: string;
  driver_vehicle_plate?: string;

  manual_action: boolean;
  eta: string;
  events: DeliveryEvent[];
  created_at: Date;
  sub_status: string;
  carrier_type: string;
  support_id: string;
  status: string;
  id: string;
  external_id: string;
  carrier_integration_id?: string;
  carrier_integration?: CarrierIntegration;
  carrier_method: CarrierMethod;
  is_batch: boolean;
  internal_route_id?: string;
  route?: {
    status: string;
  };
  method_type?: string;
  delivery_request_user?: {
    id: string;
    name: string;
    email: string;
  };
  cancellation_request_user?: {
    id: string;
    name: string;
    email: string;
  };
  scheduled_at?: string;
  expected_carrier_delivery_date?: string;
  expected_delivery_price?: number;
  delivered_delivery_price?: number;

  verification_code?: boolean;
  return_verification_code?: string;
  collect_verification_code?: string;
  // `pickup_pincode` é credencial — o dashboard-api propositalmente NÃO
  // expõe esse valor. Só o `pickup_pincode_owner` chega aqui pra decidir
  // se renderiza o bloco de validação. A comparação acontece no backend.
  pickup_pincode_owner?: 'carrier' | 'abbiamo';
  metadata?: Record<string, any> & {
    // Handshake de coleta iFood: required liga o bloco de validação;
    // validated registra que o operador confirmou o código (monotônico no
    // backend, persiste mesmo com o pedido finalizado).
    collect_handshake?: { required: boolean; validated: boolean };
  };
  // Automação que originou este envio (envio/reenvio/inatividade). Vem null
  // quando o envio não veio de automação. Oferta não é rastreada aqui — age
  // na cotação, sem carimbo na delivery.
  automation?: DeliveryAutomation | null;
}

export type DeliveryAutomationType = 'DISPATCH' | 'RESEND' | 'INACTIVITY';

export interface DeliveryAutomation {
  type: DeliveryAutomationType;
  rule_id: string;
  rule_name: string | null;
  seller_id: string | null;
}

export interface CarrierIntegration {
  id: string;
  active: boolean;
  cancellation_data?: {
    cancellation_statuses: string[];
  };
  carrier_method_group_id: string;
  carrier_method_group: CarrierMethodGroup;
  carrier_method_id: string;
  max_dispatched_time: string;
  seller_id: string;
  shipping_additional_value: number;
  shipping_factor: number;
  type: string;
  virtual_carrier_name?: string;
  data: Record<string, any>;
}

export interface CarrierMethodGroup {
  id: string;
  carrier_name: string;
  method_group_name: string;
  active: boolean;
}

export interface CarrierMethod {
  id: string;
  carrier_name: string;
  method_name: string;
  express_delivery_minutes: number;
  method_type: string;
  active: boolean;
  carrier_method_group_id: string;
}

export interface Carrier {
  name: string;
  provider?: string;
  has_delivery_cancel: boolean;
  cancellation_data?: {
    cancellation_statuses: string[];
  };
}

export interface PreviousOrderDTO {
  id: string;
  status_name: string;
  number: string;
  last_delivery_delivered_date: string;
  seller: {
    id: string;
    trading_name: string;
  };
}

export interface OrderPolyline {
  polyline: string;
}

export interface Shipper {
  id: string;
  name: string;
  identifier: string;
  document_number: string;
  email: string;
  shipper_group_id: string;
  log_seller_id?: string;
  go_seller_group_id: string;
  /** Espelhamento de falhas ligado: o GO devolve o pedido ao LOG como cancelado. */
  forward_order_failed_to_log?: boolean;
}

export interface Order {
  shipper: Shipper;
  customer: Customer;
  source_address: DestinationAddress;
  destination_address: DestinationAddress;
  volumes: Volume[];

  /** nulo = ninguém assumiu a entrega ainda (ver `last-delivery-type.ts`). */
  last_delivery_type: string | null;
  last_delivery_driver_name: string;
  last_delivery_driver_document_number: string;
  last_delivery_scheduled_at: string;
  last_delivery_delivered_date?: string;
  // Prazo da transportadora da última entrega. Base da coluna/badge de atraso
  // e do banner (via getOrderDelayBucket).
  last_delivery_expected_carrier_delivery_date?: string;

  last_delivery_latitude?: number;
  last_delivery_longitude?: number;

  // Marcador interno de risco de atraso aplicado pelo automaticamo (regra de
  // tier). Quando presente, aparece na coluna "Situação de entrega" no lugar do
  // badge genérico. Nome + cor vêm da config do marcador interno.
  internal_delay_marker?: { id: string; name: string; color: string } | null;

  carrier_delivery_delayed_status: DelayedDeliveryStatusEnum;

  operation: 'SUPPLY' | 'DELIVERY';

  manual_action: boolean;

  id: string;
  tracking: string;
  status: number;
  status_name: string;
  number: string;
  amount: number;
  shipping_fee: number;
  url: string;
  access_keys?: string | null;
  cte_access_key?: string | null;
  content_declaration?: {
    key?: string;
    serie?: string;
    number?: string;
  } | null;
  external_created_at?: string | null;
  invoice_number?: string | null;

  created_at: Date;
  status_updated_at: Date;

  eta: Date;
  arrived_at: Date;
  metadata?: any;
  carrier_metadata?: {
    thermolabile: boolean;
    keep_prescription: boolean;
    pos_terminal_required: boolean;
    return_to_origin: boolean;
  };
  external_id?: string;
  creation_origin: string;

  type: OrderTypes;
  sub_status: string;
  /** Código do último motivo de falha (delivery_events.failure_code), projetado
   * pelo BFF na busca. Usado pela nomenclatura customizada por conta. */
  failure_code?: string;
  carrier: Carrier;
  is_routeable: boolean;
  straight_estimated_distance_meters: number;
  estimated_distance_meters: number;
  seller: Seller;

  has_attachment: boolean;
  has_review: boolean;
  review_type: string;
  review_score: number;
  /** Disputa Care do pedido (enriquecido no BFF via care-service). */
  dispute_status?: 'pending' | 'resolved' | null;

  expected_customer_shipping_date?: string;
  expected_customer_shipping_price?: number;

  delivery_window_start?: Date;
  delivery_window_end?: Date;
  delivery_service_time?: number;
  delivery_start_window_hour?: string;
  delivery_end_window_hour?: string;
  residence_type?: 'commercial' | 'residential';
  seller_processed_at?: Date;

  inactivity_rule_date_to_cancel?: Date;

  previous_order: PreviousOrderDTO | null;

  nps: Nps[];
  delivery_events: DeliveryEvent[];
  deliveries: Delivery[];

  created_by_user_id?: string;
  created_by_user?: {
    id: string;
    email: string;
  };

  invoice_tags: {
    id: string;
    label: string;
    color: string;
    description: string;
  }[];

  waypoints: Waypoint[]; // deprecated
}

export interface DriverOfferTag {
  id: string;
  name: string;
  color: string;
}

export interface DriverOffer {
  driver_id: string;
  driver_name: string;
  driver_document: string;
  driver_tags: DriverOfferTag[];
  offered_at: string;
}

export interface DriverOffersResponse {
  items: DriverOffer[];
  has_more: boolean;
  page: number;
  page_size: number;
  total: number;
}

export interface SearchOrder {
  last_delivery_type: string | null;
  last_delivery_driver_name: string;
  last_delivery_driver_document_number: string;

  id: string;
  tracking: string;
  status: number;
  status_name: string;
  number: string;
  amount: number;
  shipping_fee: number;
  url: string;
  access_keys?: string | null;
  cte_access_key?: string | null;
  external_created_at?: string | null;
  invoice_number?: string | null;
  created_at: Date;
  eta: Date;
  arrived_at: Date;
  metadata?: any;
  external_id?: string;
  creation_origin: string;

  type: OrderTypes;
  sub_status: string;
  /** Código do último motivo de falha (delivery_events.failure_code), projetado
   * pelo BFF na busca. Usado pela nomenclatura customizada por conta. */
  failure_code?: string;
  carrier: Carrier;
  is_routeable: boolean;
}

export interface PaginatedResponse {
  totalOrders: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  rowsPerPag: number;
  currentPageData: Order[];
}

export interface Nps {
  response_1: number;
  response_2: number;
  response_3: number;
  answered_at: Date;
  answer_source: string;
}

export enum DelayedDeliveryStatusEnum {
  DELAYED = 'DELAYED',
  ON_TIME = 'ON_TIME',
  NONE = 'NONE',
}

export enum OrderTypes {
  TAKEOUT = 'TAKEOUT',
  DELIVERY = 'DELIVERY',
  SUPLY = 'SUPPLY',
  RETURN = 'RETURN',
}

export enum OrderStatus {
  CREATED = 'CREATED',
  DISPATCHED = 'DISPATCHED',
  START_DELIVERY = 'START_DELIVERY',
  SUCCESSFUL = 'SUCCESSFUL',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  // VISIT_LATER = 'VISIT_LATER',
  PENDING = 'PENDING',
  // TREATED = 'TREATED',
  HANDLING = 'HANDLING',
  COLLECTED = 'COLLECTED',
  RETURNING = 'RETURNING',
  RETURNED = 'RETURNED',
  IN_TRANSIT = 'IN_TRANSIT',
  ORDER_FAILED = 'ORDER_FAILED',
  MANUAL_HANDLE = 'MANUAL_HANDLE',
  ON_HOLD = 'ON_HOLD',
  SCHEDULED = 'SCHEDULED',
}

export const ORDER_STATUS_FINALIZE_ORDER = [
  OrderStatus.SUCCESSFUL,
  OrderStatus.CANCELED,
  OrderStatus.MANUAL_HANDLE,
];

export const ORDER_STATUS_FINALIZE_DELIVERY = [
  OrderStatus.ORDER_FAILED,
  OrderStatus.RETURNED,
];

export enum CustomerDocumentTypes {
  CPF = 'CPF',
  CNPJ = 'CNPJ',
  PASSPORT = 'PASSPORT',
  RUT = 'RUT',
  CI = 'CI',
  CC = 'CC',
  CE = 'CE',
  NIT = 'NIT',
  RFC = 'RFC',
  DNI = 'DNI',
  LC = 'LC',
  LE = 'LE',
  CUIT = 'CUIT',
  RUC = 'RUC',
}

export interface OrderSuggestion {
  id: string;
  number: string;
  invoice_number: string;
  tracking: string;
  status_name: string;
  sub_status: string;
  created_at: string;
  external_id?: string;
  seller: {
    id: string;
    trading_name: string;
  };
  customer: {
    id: string;
    document_number: string;
    name: string;
  };
}

export type Directions = {
  bbox?: number[] | null;
  routes?: RoutesEntity[] | null;
  metadata: Metadata;
};

export type RoutesEntity = {
  summary: Summary;
  segments?: SegmentsEntity[] | null;
  bbox?: number[] | null;
  geometry: string;
  way_points?: number[] | null;
};
export type Summary = {
  distance: number;
  duration: number;
};
export type SegmentsEntity = {
  distance: number;
  duration: number;
  steps?: StepsEntity[] | null;
};
export type StepsEntity = {
  distance: number;
  duration: number;
  type: number;
  instruction: string;
  name: string;
  way_points?: number[] | null;
};
export type Metadata = {
  attribution: string;
  service: string;
  timestamp: number;
  query: Query;
  engine: Engine;
};
export type Query = {
  coordinates?: (number[] | null)[] | null;
  profile: string;
  format: string;
};
export type Engine = {
  version: string;
  build_date: string;
  graph_date: string;
};
